import { ASSETS } from './assets-data'

const refundData = {
  en: {
    seo: {
      title: 'Refund Policy | BH Hardware',
      description: 'BH Hardware refund policy — learn about return windows, restocking fees, defective items, and how refunds are processed.',
      keywords: 'BH Hardware refund, return policy, hardware store refund, garage door parts return, bhhardware.net'
    },
    heroSection: {
      image: "/placeholder.svg",
      overlay: "bg-black/60"
    },
    title: 'Refund Policy',
    effectiveDateLabel: 'Last Updated',
    effectiveDate: 'October 1, 2025',

    introParagraphs: [
      'Our policy lasts 30 days. If 30 days have gone by since your purchase, unfortunately we cannot offer you a refund or exchange.',
      'To be eligible for a return, your item must be unused and in the same condition that you received it, and it must be in the original packaging.'
    ],

    sections: [
      {
        id: 'eligibility',
        number: 1,
        heading: 'Return Eligibility',
        paragraphs: [
          'To complete your return, we require a receipt or proof of purchase.',
          'There are certain situations where only partial refunds are granted: any item not in its original condition, is damaged or missing parts for reasons not due to our error; or any item that is returned more than 30 days after delivery.'
        ]
      },
      {
        id: 'refunds',
        number: 2,
        heading: 'Refunds',
        paragraphs: [
          'Once your return is received and inspected, we will send you an email to notify you that we have received your returned item. We will also notify you of the approval or rejection of your refund.',
          'All refunds are subject to restocking fees of up to 20% depending on condition of item, and return costs.',
          'If you are approved, your refund will be processed and a credit will automatically be applied to your credit card or original method of payment within a certain number of days.'
        ]
      },
      {
        id: 'late-refunds',
        number: 3,
        heading: 'Late or Missing Refunds',
        paragraphs: (info) => [
          'If you haven\'t received a refund yet, first check your bank account again.',
          'Then contact your credit card company — it may take some time before your refund is officially posted.',
          'Next, contact your bank. There is often some processing time before a refund is posted.',
          `If you've done all of this and still have not received your refund, please contact us at ${info.email || 'admin@bh-hardware.net'}.`
        ]
      },
      {
        id: 'sale-items',
        number: 4,
        heading: 'Sale Items',
        paragraphs: [
          'Only regular priced items may be refunded. Unfortunately, sale items cannot be refunded.'
        ]
      },
      {
        id: 'exchanges',
        number: 5,
        heading: 'Exchanges',
        paragraphs: (info) => [
          `We only replace items if they are defective or damaged. If you need to exchange an item for the same product, send us an email at ${info.email || 'admin@bh-hardware.net'} and send your item to: ${info.businessName || 'BH Hardware'}, ${info.address || '8922 N Fork Dr, North Fort Myers FL 33903 US'}.`
        ]
      },
      {
        id: 'gifts',
        number: 6,
        heading: 'Gifts',
        paragraphs: [
          'If the item was marked as a gift when purchased and shipped directly to you, you\'ll receive a gift credit for the value of your return. Once the returned item is received, a gift certificate will be mailed to you.',
          'If the item wasn\'t marked as a gift when purchased, we will send the refund to the original purchaser.'
        ]
      },
      {
        id: 'return-shipping',
        number: 7,
        heading: 'Return Shipping',
        paragraphs: (info) => [
          `To return your product, mail it to: ${info.businessName || 'BH Hardware'}, ${info.address || '8922 N Fork Dr, North Fort Myers FL 33903 US'}.`,
          'You will be responsible for paying for your own shipping costs for returning your item. Shipping costs are non-refundable. If you receive a refund, the cost of return shipping will be deducted from your refund.',
          'If you are shipping an item over $75, you should consider using a trackable shipping service or purchasing shipping insurance. We don\'t guarantee that we will receive your returned item.',
          'ALL CUSTOM TORSION SPRINGS ARE NON-REFUNDABLE.'
        ]
      }
    ],

    contactTitle: 'Refund Questions?',
    contactIntro: (info) => `If you have questions about your refund or return, contact us at ${info.email || 'admin@bh-hardware.net'} or call ${info.phone || '+1 855-786-7504'}.`,

    conversionSection: {
      title: 'We Stand Behind Our Products',
      subtitle: 'Customer satisfaction is our priority. If there\'s an issue with your order, we\'ll work with you to make it right.',
      bgImage: "https://sawpy-assets.s3.us-east-2.amazonaws.com/BH-Hardware/backgrounds/warehouse_bg.jpg",
      bgColor: "bg-gray-50 dark:bg-zinc-900",
      buttonText: 'Return Home'
    }
  },

  he: {
    seo: {
      title: 'מדיניות החזרים | BH Hardware',
      description: 'מדיניות החזרים של BH Hardware — חלונות זמן להחזרה, דמי טיפול, פריטים פגומים ואופן עיבוד ההחזרים.',
      keywords: 'החזר כספי BH Hardware, מדיניות החזרה, bhhardware.net'
    },
    heroSection: {
      image: "https://sawpy-assets.s3.us-east-2.amazonaws.com/BH-Hardware/backgrounds/warehouse_bg.jpg",
      overlay: "bg-black/60"
    },
    title: 'מדיניות החזרים',
    effectiveDateLabel: 'עודכן לאחרונה',
    effectiveDate: '1 באוקטובר 2025',

    introParagraphs: [
      'המדיניות שלנו בתוקף ל-30 יום. אם עברו 30 יום מהרכישה, לצערנו לא נוכל להציע החזר כספי או החלפה.',
      'כדי להיות זכאי להחזרה, הפריט חייב להיות ללא שימוש, במצב שבו קיבלתם אותו ובאריזה המקורית.'
    ],

    sections: [
      {
        id: 'eligibility',
        number: 1,
        heading: 'זכאות להחזרה',
        paragraphs: [
          'לביצוע ההחזרה, נדרש קבלה או הוכחת רכישה.',
          'ישנם מקרים בהם ניתן לקבל רק החזר חלקי: פריט שאינו במצב מקורי, פגום או חסרים בו חלקים; או פריט שהוחזר יותר מ-30 יום לאחר האספקה.'
        ]
      },
      {
        id: 'refunds',
        number: 2,
        heading: 'החזרים כספיים',
        paragraphs: [
          'לאחר קבלת הפריט המוחזר ובדיקתו, נשלח לכם הודעת אימייל ועל אישור או דחיית ההחזר הכספי.',
          'כל ההחזרים כפופים לדמי טיפול של עד 20% בהתאם למצב הפריט ועלויות ההחזרה.',
          'אם אושרתם, ההחזר יעובד ויזוכה לכרטיס האשראי שלכם או לאמצעי התשלום המקורי.'
        ]
      },
      {
        id: 'sale-items',
        number: 3,
        heading: 'פריטי מבצע',
        paragraphs: [
          'רק פריטים במחיר מלא ניתנים להחזר. לצערנו, פריטי מבצע אינם ניתנים להחזר כספי.'
        ]
      },
      {
        id: 'exchanges',
        number: 4,
        heading: 'החלפות',
        paragraphs: (info) => [
          `אנו מחליפים פריטים רק אם הם פגומים או ניזוקו. שלחו אימייל ל-${info.email || 'admin@bh-hardware.net'} ושלחו את הפריט ל: ${info.businessName || 'BH Hardware'}, ${info.address || '8922 N Fork Dr, North Fort Myers FL 33903 US'}.`
        ]
      },
      {
        id: 'return-shipping',
        number: 5,
        heading: 'משלוח החזרה',
        paragraphs: (info) => [
          `לשליחת הפריט בחזרה, שלחו אותו ל: ${info.businessName || 'BH Hardware'}, ${info.address || '8922 N Fork Dr, North Fort Myers FL 33903 US'}.`,
          'הלקוח אחראי לעלויות משלוח ההחזרה. עלויות משלוח אינן ניתנות להחזר.',
          'כל קפיצי תלייה מותאמים אישית אינם ניתנים להחזר.'
        ]
      }
    ],

    contactTitle: 'שאלות על החזרים?',
    contactIntro: (info) => `לשאלות בנוגע להחזר כספי, צרו קשר בכתובת ${info.email || 'admin@bh-hardware.net'} או התקשרו ל-${info.phone || '+1 855-786-7504'}.`,

    conversionSection: {
      title: 'אנחנו עומדים מאחורי המוצרים שלנו',
      subtitle: 'שביעות רצון הלקוח היא העדיפות שלנו. אם יש בעיה עם הזמנתכם, נעבוד אתכם כדי לפתור אותה.',
      bgImage: "https://sawpy-assets.s3.us-east-2.amazonaws.com/BH-Hardware/backgrounds/warehouse_bg.jpg",
      bgColor: "bg-gray-50 dark:bg-zinc-900",
      buttonText: 'חזרה לדף הבית'
    }
  }
};

export function getRefundStrings(language) {
  return refundData[language] || refundData.en;
}

export default refundData;
